// Admin PDF jobs dashboard. Mirrors the messenger-press admin pattern
// but focused on GeneratedPdf rows so admins can monitor and resubmit
// failed PDF render jobs without pinging engineering.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Redirect } from 'react-router-dom'
import { useRecoilValue } from 'recoil'
import styled from 'styled-components'
import colors from 'utils/styling/colors'
import { userAtom } from '@/atoms/_globalAtoms'
import { useIsAdmin } from '@/hooks/utility/useIsAdmin'
import { Navigation } from 'components'
import { FullscreenModal } from '@/components/global'
import { Button } from '@/components/priority'
import Flex from '@/components/priority/Flex/Flex'
import Text from '@/components/priority/Text/Text'
import {
  listPdfJobs,
  resubmitPdfJob,
  type PdfJobRow,
  type ListPdfJobsQuery,
} from '@/api/pdf-admin'
import type { PdfStatusKey } from '@messenger/types'

const TID_PAGE = 'pdf-jobs-page'
const TID_TABLE = 'pdf-jobs-table'
const TID_ROW = 'pdf-jobs-row-'
const TID_RESUBMIT = 'pdf-jobs-resubmit-'
const TID_ERROR = 'pdf-jobs-error-'

const POLL_INTERVAL_MS = 3_000
const PAGE_SIZE = 20

const TERMINAL_STATUSES: readonly string[] = ['COMPLETE', 'ERROR', 'TIMEOUT']
const RESUBMITTABLE: readonly string[] = [
  'ERROR',
  'TIMEOUT',
  'IN_QUEUE',
  'GENERATING',
]
const PROCESSING_STATUSES: readonly string[] = ['IN_QUEUE', 'GENERATING']
const FAILED_STATUSES: readonly string[] = ['ERROR', 'TIMEOUT']

const STATUS_OPTIONS: PdfStatusKey[] = [
  'IN_QUEUE',
  'GENERATING',
  'COMPLETE',
  'ERROR',
  'TIMEOUT',
]

const isTerminal = (s: string) => TERMINAL_STATUSES.includes(s)

export const PdfJobsPage = () => {
  const isAdmin = useIsAdmin()
  const user = useRecoilValue(userAtom)

  const [jobs, setJobs] = useState<PdfJobRow[]>([])
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [confirmStopId, setConfirmStopId] = useState<string | null>(null)
  const [filters, setFilters] = useState<{
    status: PdfStatusKey | ''
    onlyFinals: boolean
    orderId: string
  }>({ status: '', onlyFinals: false, orderId: '' })

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const cancelledRef = useRef(false)
  const latestReq = useRef(0)

  const query = useMemo<ListPdfJobsQuery>(
    () => ({
      page,
      pageSize: PAGE_SIZE,
      status: filters.status === '' ? undefined : filters.status,
      onlyFinals: filters.onlyFinals || undefined,
      orderId: filters.orderId.trim() || undefined,
    }),
    [page, filters],
  )

  const fetchJobs = useCallback(async () => {
    const requestId = ++latestReq.current
    try {
      const response = await listPdfJobs(query)
      if (cancelledRef.current || requestId !== latestReq.current) return
      setJobs(response.data.items)
      setTotal(response.data.total)
      setError(null)
    } catch (err) {
      if (cancelledRef.current || requestId !== latestReq.current) return
      setError((err as Error).message ?? 'Failed to load PDF jobs')
    } finally {
      if (!cancelledRef.current && requestId === latestReq.current) {
        setLoading(false)
      }
    }
  }, [query])

  useEffect(() => {
    cancelledRef.current = false
    setLoading(true)
    fetchJobs()
    return () => {
      cancelledRef.current = true
    }
  }, [fetchJobs])

  const hasInFlight = useMemo(
    () => jobs.some((j) => !isTerminal(j.status)),
    [jobs],
  )

  useEffect(() => {
    if (!hasInFlight) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      return
    }
    if (intervalRef.current) return
    intervalRef.current = setInterval(fetchJobs, POLL_INTERVAL_MS)
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [hasInFlight, fetchJobs])

  const performResubmit = async (id: string) => {
    setBusyId(id)
    try {
      await resubmitPdfJob(id)
      await fetchJobs()
    } catch (err) {
      setError(
        (err as { response?: { data?: { error?: string } } })?.response?.data
          ?.error ||
          (err as Error).message ||
          'Resubmit failed',
      )
    } finally {
      setBusyId(null)
    }
  }

  const handleResubmit = async (id: string, currentStatus: string) => {
    if (PROCESSING_STATUSES.includes(currentStatus)) {
      setConfirmStopId(id)
      return
    }
    await performResubmit(id)
  }

  const handleConfirmStop = async () => {
    const id = confirmStopId
    setConfirmStopId(null)
    if (!id) return
    await performResubmit(id)
  }

  if (user === null) return null
  if (!isAdmin) return <Redirect to="/" />

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const canPrev = page > 1
  const canNext = page < totalPages

  return (
    <>
      <Navigation type="admin" />
      <PageWrap data-testid={TID_PAGE}>
        <Header>
          <Title>PDF Jobs</Title>
          <Subtitle>
            Monitor every PDF rendering job — digital proofs and final PDFs —
            across all funeral homes and orders. Use this page to track job
            status in real time, stop a job that&apos;s stuck processing, and
            resubmit any job that failed, timed out, or hung.
          </Subtitle>
        </Header>

        <FiltersRow>
          <FilterGroup>
            <label htmlFor="pdf-jobs-status-filter">Status</label>
            <select
              id="pdf-jobs-status-filter"
              value={filters.status}
              onChange={(e) => {
                setPage(1)
                setFilters((f) => ({
                  ...f,
                  status: e.target.value as PdfStatusKey | '',
                }))
              }}
            >
              <option value="">All</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </FilterGroup>

          <FilterGroup>
            <label htmlFor="pdf-jobs-order-id-filter">Order ID</label>
            <input
              id="pdf-jobs-order-id-filter"
              type="text"
              value={filters.orderId}
              placeholder="Filter by order id"
              onChange={(e) => {
                setPage(1)
                setFilters((f) => ({ ...f, orderId: e.target.value }))
              }}
            />
          </FilterGroup>

          <FilterGroup>
            <label>
              <input
                type="checkbox"
                checked={filters.onlyFinals}
                onChange={(e) => {
                  setPage(1)
                  setFilters((f) => ({ ...f, onlyFinals: e.target.checked }))
                }}
              />
              Only final PDFs
            </label>
          </FilterGroup>

          <RefreshButton onClick={fetchJobs}>Refresh</RefreshButton>
        </FiltersRow>

        {loading && <Status>Loading PDF jobs…</Status>}
        {error && <ErrorStatus>Error: {error}</ErrorStatus>}
        {!loading && !error && jobs.length === 0 && (
          <Status>No PDF jobs match the current filters.</Status>
        )}

        {jobs.length > 0 && (
          <>
            <TableScroller>
              <Table data-testid={TID_TABLE}>
                <thead>
                  <tr>
                    <Th>Status</Th>
                    <Th>Progress</Th>
                    <Th>Kind</Th>
                    <Th>PDF Type</Th>
                    <Th>Deceased</Th>
                    <Th>Product</Th>
                    <Th>Funeral Home</Th>
                    <Th>Created</Th>
                    <Th>Updated</Th>
                    <Th>Created By</Th>
                    <Th>Job ID</Th>
                    <Th>Order ID</Th>
                    <Th>Order Product ID</Th>
                    <Th>Case ID</Th>
                    <Th>Page Set ID</Th>
                    <Th>Error</Th>
                    <Th>Actions</Th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.map((job) => {
                    // Anyone-viewable rule: show the View action whenever
                    // the row has a resolvable link. For digital proofs
                    // that's `galleryUrl`, which the worker populates as
                    // soon as screenshots land — the proof row's own
                    // PdfStatus doesn't have to be COMPLETE for the
                    // gallery page to be browseable.
                    const viewHref = job.viewUrl ?? null
                    const resubmittable =
                      RESUBMITTABLE.includes(job.status) && !!job.jobPayload
                    const isProcessing = PROCESSING_STATUSES.includes(
                      job.status,
                    )
                    const kindLabel =
                      job.jobKind === 'digital-proof'
                        ? 'Digital Proof'
                        : 'Final PDF'
                    const viewLabel =
                      job.jobKind === 'digital-proof' ? 'View Proof' : 'View'
                    const resubmitIdleLabel = isProcessing
                      ? 'Stop & Resubmit'
                      : 'Resubmit'
                    const resubmitBusyLabel = isProcessing
                      ? 'Stopping…'
                      : 'Resubmitting…'
                    // Only an ERROR or TIMEOUT row shows a reason: its
                    // errorMessage, or its url when it has none. Any other
                    // row's url is its file, and its errorMessage can be
                    // left from an attempt that failed before it finished.
                    const failureText = FAILED_STATUSES.includes(job.status)
                      ? job.errorMessage || job.url
                      : null
                    return (
                      <tr key={job.id} data-testid={`${TID_ROW}${job.id}`}>
                        <Td>
                          <StatusPill $status={job.status}>
                            {job.status}
                          </StatusPill>
                        </Td>
                        <Td>{job.progress}%</Td>
                        <Td>{kindLabel}</Td>
                        <Td>{job.pdfType || '—'}</Td>
                        <Td>{job.caseName || '—'}</Td>
                        <Td>{job.productName || '—'}</Td>
                        <Td>{job.funeralHomeName || '—'}</Td>
                        <Td>{new Date(job.createdAt).toLocaleString()}</Td>
                        <Td>{new Date(job.updatedAt).toLocaleString()}</Td>
                        <Td>
                          {job.createdBy ? job.createdBy.slice(0, 8) : '—'}
                        </Td>
                        <Td>
                          <Mono>{job.id}</Mono>
                        </Td>
                        <Td>
                          <Mono>{job.orderId ?? '—'}</Mono>
                        </Td>
                        <Td>
                          <Mono>{job.productId ?? '—'}</Mono>
                        </Td>
                        <Td>
                          <Mono>{job.caseId ?? '—'}</Mono>
                        </Td>
                        <Td>
                          <Mono>{job.orderPageSetId ?? '—'}</Mono>
                        </Td>
                        <Td>
                          <ErrorCell
                            data-testid={`${TID_ERROR}${job.id}`}
                            title={failureText || ''}
                          >
                            {failureText || '—'}
                          </ErrorCell>
                        </Td>
                        <Td>
                          <ActionGroup>
                            {viewHref && (
                              <DownloadLink
                                href={viewHref}
                                target="_blank"
                                rel="noreferrer"
                              >
                                {viewLabel}
                              </DownloadLink>
                            )}
                            {resubmittable && (
                              <ResubmitButton
                                data-testid={`${TID_RESUBMIT}${job.id}`}
                                disabled={busyId === job.id}
                                onClick={() =>
                                  handleResubmit(job.id, job.status)
                                }
                              >
                                {busyId === job.id
                                  ? resubmitBusyLabel
                                  : resubmitIdleLabel}
                              </ResubmitButton>
                            )}
                            {!viewHref && !resubmittable && '—'}
                          </ActionGroup>
                        </Td>
                      </tr>
                    )
                  })}
                </tbody>
              </Table>
            </TableScroller>

            <PaginationBar>
              <PageInfo>
                Page {page} of {totalPages} · {total} job
                {total === 1 ? '' : 's'}
              </PageInfo>
              <PageButtons>
                <PageButton
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={!canPrev}
                >
                  Prev
                </PageButton>
                <PageButton
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={!canNext}
                >
                  Next
                </PageButton>
              </PageButtons>
            </PaginationBar>
          </>
        )}
      </PageWrap>
      {confirmStopId &&
        createPortal(
          <FullscreenModal
            show={[true, () => setConfirmStopId(null)]}
            hideClose={false}
          >
            <Flex column full padding="32px" maxWidth="600px">
              <Text size="20px" weight="600" margin="10px 0 10px 0">
                Stop & Resubmit Job?
              </Text>
              <Text size="16px" margin="10px 0 10px 0">
                This job is still processing. Stopping it will cancel the
                current attempt and start a new one. Continue?
              </Text>
              <Flex justify="flex-end" margin="10px 0 0 0" gap="8px">
                <Button onClick={() => setConfirmStopId(null)}>Cancel</Button>
                <Button primary destructive onClick={handleConfirmStop}>
                  Stop & Resubmit
                </Button>
              </Flex>
            </Flex>
          </FullscreenModal>,
          document.body,
        )}
    </>
  )
}

export default PdfJobsPage

const PageWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 32px 32px 80px 32px;
  max-width: 1600px;
  margin: 0 auto;
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  margin: 0;
  color: ${colors('almostBlack')};
`

const Subtitle = styled.p`
  font-size: 14px;
  margin: 0;
  color: ${colors('gray2')};
`

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-end;
  background: ${colors('palePrimary')};
  padding: 16px;
  border-radius: 8px;
`

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
  color: ${colors('gray2')};
  font-weight: 600;

  select,
  input[type='text'] {
    padding: 6px 8px;
    border: 1px solid ${colors('gray4')};
    border-radius: 6px;
    font-size: 13px;
    background: ${colors('white')};
    color: ${colors('almostBlack')};
  }

  label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-weight: 600;
  }
`

const RefreshButton = styled.button`
  padding: 8px 14px;
  border-radius: 6px;
  border: 1px solid ${colors('gray4')};
  background: ${colors('white')};
  color: ${colors('almostBlack')};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
`

// Horizontal scroll wrapper — full UUIDs + joined case/funeral-home
// names push the table wider than the page, so overflow lives on this
// container instead of clipping or wrapping individual cells.
const TableScroller = styled.div`
  width: 100%;
  overflow-x: auto;
  border-radius: 8px;
  background: ${colors('white')};
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: ${colors('white')};
  border-radius: 8px;
  overflow: hidden;
  table-layout: auto;
  white-space: nowrap;
`

// Error cell clamps noisy stack-trace messages to a single line but
// preserves the full text in the browser tooltip (title attr) so
// admins can still copy the message when they need to dig in.
const ErrorCell = styled.span`
  display: inline-block;
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
  color: ${colors('error')};
  font-size: 12px;
`

const Th = styled.th`
  text-align: left;
  padding: 10px 12px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${colors('gray2')};
  background: ${colors('paleSecondary')};
`

const Td = styled.td`
  padding: 10px 12px;
  font-size: 13px;
  border-top: 1px solid ${colors('gray4')};
  color: ${colors('almostBlack')};
`

const Mono = styled.span`
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, monospace;
  font-size: 12px;
`

const STATUS_COMPLETE_BG = '#059669'
const StatusPill = styled.span<{ $status: string }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  background: ${({ $status }) => {
    if ($status === 'COMPLETE') return STATUS_COMPLETE_BG
    if ($status === 'ERROR' || $status === 'TIMEOUT') return colors('error')
    return colors('gray3')
  }};
  color: white;
`

const ActionGroup = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`

const DownloadLink = styled.a`
  padding: 6px 10px;
  border-radius: 6px;
  background: ${colors('brand')};
  color: white;
  font-size: 12px;
  font-weight: 600;
  text-decoration: none;
`

const ResubmitButton = styled.button`
  padding: 6px 10px;
  border-radius: 6px;
  border: none;
  background: #dc2626;
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  &:hover:not(:disabled) {
    background: #b91c1c;
  }
`

const Status = styled.div`
  padding: 12px;
  font-size: 13px;
  color: ${colors('gray2')};
`

const ErrorStatus = styled(Status)`
  color: ${colors('error')};
`

const PaginationBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
`

const PageInfo = styled.div`
  font-size: 12px;
  color: ${colors('gray2')};
`

const PageButtons = styled.div`
  display: flex;
  gap: 8px;
`

const PageButton = styled.button`
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid ${colors('gray4')};
  background: ${colors('white')};
  color: ${colors('almostBlack')};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`
